import { t } from "@travily/i18n";
import { tv } from "@ui/lib/tv";
import { type NotificationAction, NotificationItem, type NotificationItemProps } from "./NotificationItem";

export type Notification = Omit<NotificationItemProps, "onAction"> & { id: number; kind: string };

export type NotificationPanelVariant = "popover" | "screen";

const notificationPanel = tv({
  slots: {
    container: "flex flex-col",
    list: "flex flex-col divide-y divide-neutral-100",
  },
  variants: {
    variant: {
      // The floating card chrome (surface, width) comes from the PopoverSurface
      // the bell renders the panel into; this is only the inner layout.
      popover: {
        container: "gap-4 px-5 py-4",
        list: "max-h-[400px] overflow-y-auto",
      },
      screen: {
        container: "w-full py-4",
      },
    },
  },
});

export interface NotificationPanelProps {
  notifications: Notification[];
  onAction: (action: NotificationAction) => void;
  /** `popover` (default) is the floating desktop card. `screen` is the full-page
   *  mobile presentation — the parent screen owns the title, so the panel drops
   *  its chrome and lets the parent control width and scroll. */
  variant?: NotificationPanelVariant;
}

export function NotificationPanel({ notifications, onAction, variant = "popover" }: NotificationPanelProps) {
  const styles = notificationPanel({ variant });

  return (
    <div className={styles.container()}>
      {variant === "popover" && (
        <div className="border-b border-neutral-100 pb-3">
          <h2 className="text-2xl/8 font-extrabold tracking-tight text-neutral-900">
            {t("components.views.shared.notification_panel.title")}
          </h2>
        </div>
      )}
      <div className={styles.list()}>
        {notifications.length === 0 ? (
          <p className="py-8 text-center text-base text-neutral-400">
            {t("components.views.shared.notification_panel.empty")}
          </p>
        ) : (
          notifications.map((notification) => (
            <div className="py-4 first:pt-0 last:pb-0" key={notification.id}>
              <NotificationItem {...notification} onAction={onAction} />
            </div>
          ))
        )}
      </div>
    </div>
  );
}
